/** DNS_CNAME_RECORD control: live-read the operator CNAME from Route53 (where it currently points)
 *  and UPSERT it (create/update) via the execute endpoint. AWS uses the EC2/EKS instance role.
 */
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { ConsoleApiError } from '@/api/client';
import { items } from '@/api/preflight';
import type { DnsCnameDetail, PreflightItem } from '@/api/preflight';
import { useSnackbar } from '@/components/Snackbar';
import { ValueActions } from '@/components/ValueActions/ValueActions';

import { copyOnlyActions } from '../tenantLinks';

export function DnsCnameControl({
  item,
  topId,
  canManage,
  onChanged,
}: {
  item: PreflightItem;
  topId: number;
  canManage: boolean;
  onChanged: () => void;
}) {
  const snackbar = useSnackbar();
  const seed = item.detail as unknown as DnsCnameDetail;
  const query = useQuery({
    queryKey: ['preflight', 'dnsCnameStatus', topId, item.id],
    queryFn: () => items.fetchDnsCnameStatus(topId, item.id),
    enabled: canManage,
  });
  const status = query.data;
  const detail = status?.detail ?? seed;

  // When the live probe finds an existing CNAME, the backend marks the item completed; refetch the
  // list so the row reflects it (guarded on item.status so it runs once).
  useEffect(() => {
    if (status?.ok && status.detail?.exists && item.status !== 'completed') {
      onChanged();
    }
  }, [status, item.status, onChanged]);

  const create = useMutation({
    mutationFn: () => items.execute(topId, item.id),
    onSuccess: (res) => {
      const run = res.run;
      if (run && run.status === 'error') {
        snackbar.show(run.error_message || 'Could not write the CNAME.', 'error');
      } else {
        snackbar.show(run?.summary || 'CNAME record written.', 'success');
      }
      void query.refetch();
      onChanged();
    },
    onError: (e: unknown) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Could not write the CNAME.', 'error'),
  });

  if (status?.reason === 'not_configured' || detail?.configured === false) {
    return (
      <Box sx={{ mb: 1.5 }}>
        <Alert severity="info">
          The Route53 hosted zone / load-balancer target isn&apos;t configured on the server.
        </Alert>
      </Box>
    );
  }

  const exists = detail?.exists === true;
  const matches = detail?.matches === true;
  return (
    <Box sx={{ mb: 1.5 }}>
      <Stack spacing={1.5}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'max-content 1fr',
            columnGap: 1.5,
            rowGap: 0.25,
            alignItems: 'center',
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Hostname
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
            <ValueActions actions={copyOnlyActions(detail?.hostname, 'Copy hostname')}>
              {detail?.hostname || '—'}
            </ValueActions>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Target
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
            <ValueActions actions={copyOnlyActions(detail?.target, 'Copy target')}>
              {detail?.target || '—'}
            </ValueActions>
          </Typography>
          {exists && (
            <>
              <Typography variant="caption" color="text.secondary">
                Currently points to
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                <ValueActions actions={copyOnlyActions(detail?.current_target, 'Copy current target')}>
                  {detail?.current_target || '—'}
                </ValueActions>
              </Typography>
            </>
          )}
        </Box>

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          {exists ? (
            <CheckCircleIcon fontSize="small" color={matches ? 'success' : 'warning'} />
          ) : (
            <RadioButtonUncheckedIcon fontSize="small" color="disabled" />
          )}
          <Typography variant="body2">
            {!exists
              ? 'No CNAME record yet'
              : matches
                ? 'CNAME points to the platform load balancer'
                : 'CNAME exists but points elsewhere'}
          </Typography>
          {query.isFetching && <CircularProgress size={14} />}
        </Stack>

        {canManage && (
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="contained"
              onClick={() => create.mutate()}
              disabled={create.isPending}
              startIcon={create.isPending ? <CircularProgress size={14} /> : undefined}
            >
              {exists ? 'Update CNAME record' : 'Create CNAME record'}
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => void query.refetch()}
              disabled={query.isFetching}
            >
              Refresh
            </Button>
          </Stack>
        )}
      </Stack>
    </Box>
  );
}
